var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { loadingState } from '@/components/LoadingView';
import { UpvoteTargetTypeDto } from '@/generated-sources/openapi';
import useAppComponents from '@/hooks/useAppComponents';
import { updateSearchCacheForPlaceAsync } from '@/utils/SearchPlacesUtils';
import ToastUtils from '@/utils/ToastUtils';
export function useDeleteReview({ type, reviewId, placeId, }) {
    const { api } = useAppComponents();
    const [loading, setLoading] = useAtom(loadingState);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => __awaiter(this, void 0, void 0, function* () {
            if (type === 'place') {
                return yield api.deletePlaceReviewPost({
                    placeReviewId: reviewId,
                });
            }
            else {
                return yield api.deleteToiletReviewPost({
                    toiletReviewId: reviewId,
                });
            }
        }),
        onMutate: () => setLoading(new Map(loading).set('PlaceDetailV2', true)),
        onSuccess: (_data, _variables) => {
            if (type === 'place') {
                queryClient.invalidateQueries({
                    queryKey: ['PlaceDetailV2', placeId, UpvoteTargetTypeDto.PlaceReview],
                });
                // 내 리뷰 > 내가 작성한 리뷰 리스트
                queryClient.invalidateQueries({
                    queryKey: ['MyReviews', UpvoteTargetTypeDto.PlaceReview],
                });
                // 내 리뷰 > 도움이 돼요 리스트
                queryClient.invalidateQueries({
                    queryKey: ['ReviewsUpvoted', UpvoteTargetTypeDto.PlaceReview],
                });
                // 내 리뷰 > 내가 작성한 리뷰 통계
                queryClient.invalidateQueries({
                    queryKey: [
                        'ReviewHistory',
                        'Review',
                        UpvoteTargetTypeDto.PlaceReview,
                    ],
                });
                // 내 리뷰 > 도움이 돼요 통계
                queryClient.invalidateQueries({
                    queryKey: [
                        'ReviewHistory',
                        'Upvote',
                        UpvoteTargetTypeDto.PlaceReview,
                    ],
                });
                ToastUtils.show('장소 리뷰를 삭제했습니다.');
            }
            else {
                queryClient.invalidateQueries({
                    queryKey: [
                        'PlaceDetailV2',
                        placeId,
                        UpvoteTargetTypeDto.ToiletReview,
                    ],
                });
                // 내 리뷰 > 내가 작성한 리뷰 리스트
                queryClient.invalidateQueries({
                    queryKey: ['MyReviews', UpvoteTargetTypeDto.ToiletReview],
                });
                // 내 리뷰 > 도움이 돼요 리스트
                queryClient.invalidateQueries({
                    queryKey: ['ReviewsUpvoted', UpvoteTargetTypeDto.ToiletReview],
                });
                // 내 리뷰 > 내가 작성한 리뷰 통계
                queryClient.invalidateQueries({
                    queryKey: [
                        'ReviewHistory',
                        'Review',
                        UpvoteTargetTypeDto.ToiletReview,
                    ],
                });
                // 내 리뷰 > 도움이 돼요 통계
                queryClient.invalidateQueries({
                    queryKey: [
                        'ReviewHistory',
                        'Upvote',
                        UpvoteTargetTypeDto.ToiletReview,
                    ],
                });
                ToastUtils.show('화장실 리뷰를 삭제했습니다.');
            }
            // 내 리뷰 > 리뷰 리포트 통계
            queryClient.invalidateQueries({
                queryKey: ['ReviewReport'],
            });
            // Asynchronously update search cache with full latest data
            updateSearchCacheForPlaceAsync(api, queryClient, placeId);
        },
        onError: (_error, _variables) => {
            if (type === 'place') {
                ToastUtils.show('삭제할 수 없는 리뷰입니다.');
            }
            else {
                ToastUtils.show('삭제할 수 없는 화장실 리뷰입니다.');
            }
        },
        onSettled: () => {
            setLoading(new Map(loading).set('PlaceDetailV2', false));
        },
    });
}
